import { TextBox } from './TextBox';

export type TypewriterAdvanceResult = 'revealed' | 'readyForNext';

export interface TypewriterOptions {
  textBox?: TextBox;
  textSpeed?: number;
}

export interface TypewriterState {
  text: string;
  visibleText: string;
  visibleCharacterCount: number;
  totalCharacterCount: number;
  complete: boolean;
}

const MIN_CHARS_PER_SECOND = 18;
const MAX_CHARS_PER_SECOND = 96;
const INSTANT_SPEED_THRESHOLD = 0.99;

export class Typewriter {
  private textBox: TextBox | null;
  private textCharacters: string[] = [];
  private visibleCharacterCount = 0;
  private elapsedCharacters = 0;
  private textSpeed: number;
  private complete = true;

  public constructor(options: TypewriterOptions = {}) {
    this.textBox = options.textBox ?? null;
    this.textSpeed = normalizeSpeed(options.textSpeed ?? 0.55);
  }

  public setTextBox(textBox: TextBox | null): void {
    this.textBox = textBox;
    this.render();
  }

  public setTextSpeed(textSpeed: number): void {
    this.textSpeed = normalizeSpeed(textSpeed);

    if (!this.complete && this.textSpeed >= INSTANT_SPEED_THRESHOLD) {
      this.revealAll();
    }
  }

  public start(text: string): void {
    this.textCharacters = Array.from(text);
    this.visibleCharacterCount =
      this.textSpeed >= INSTANT_SPEED_THRESHOLD ? this.textCharacters.length : 0;
    this.elapsedCharacters = this.visibleCharacterCount;
    this.complete = this.visibleCharacterCount >= this.textCharacters.length;
    this.render();
  }

  public update(deltaMs: number): void {
    if (this.complete || this.textCharacters.length === 0) {
      return;
    }

    const safeDeltaMs = Math.max(0, deltaMs);
    const charsPerSecond = getCharsPerSecond(this.textSpeed);
    this.elapsedCharacters += (charsPerSecond * safeDeltaMs) / 1000;
    const nextVisibleCount = Math.min(
      this.textCharacters.length,
      Math.floor(this.elapsedCharacters)
    );

    if (nextVisibleCount !== this.visibleCharacterCount) {
      this.visibleCharacterCount = nextVisibleCount;
      this.render();
    }

    if (this.visibleCharacterCount >= this.textCharacters.length) {
      this.complete = true;
      this.visibleCharacterCount = this.textCharacters.length;
      this.elapsedCharacters = this.visibleCharacterCount;
      this.render();
    }
  }

  public revealAll(): void {
    this.visibleCharacterCount = this.textCharacters.length;
    this.elapsedCharacters = this.visibleCharacterCount;
    this.complete = true;
    this.render();
  }

  public handleAdvance(): TypewriterAdvanceResult {
    if (!this.complete) {
      this.revealAll();
      return 'revealed';
    }

    return 'readyForNext';
  }

  public isComplete(): boolean {
    return this.complete;
  }

  public getState(): TypewriterState {
    return {
      text: this.textCharacters.join(''),
      visibleText: this.getVisibleText(),
      visibleCharacterCount: this.visibleCharacterCount,
      totalCharacterCount: this.textCharacters.length,
      complete: this.complete
    };
  }

  private render(): void {
    this.textBox?.setText(this.getVisibleText());
    this.textBox?.setPromptVisible(this.complete && this.textCharacters.length > 0);
  }

  private getVisibleText(): string {
    return this.textCharacters.slice(0, this.visibleCharacterCount).join('');
  }
}

function getCharsPerSecond(textSpeed: number): number {
  return MIN_CHARS_PER_SECOND + (MAX_CHARS_PER_SECOND - MIN_CHARS_PER_SECOND) * textSpeed;
}

function normalizeSpeed(textSpeed: number): number {
  if (!Number.isFinite(textSpeed)) {
    return 0.55;
  }

  return Math.min(Math.max(textSpeed, 0), 1);
}
